/**
 * Access token storage.
 *
 * In memory only -- a module variable, not React state and never localStorage.
 * This application holds health data, so a token in localStorage would be one
 * XSS away from a breach. The refresh token is an HttpOnly cookie the JS in
 * this file cannot read at all.
 */
let accessToken: string | null = null;
/** Epoch ms at which `accessToken` stops being accepted. */
let expiresAt = 0;
let unauthorizedHandler: (() => void) | null = null;
let refreshedHandler: ((session: unknown) => void) | null = null;

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(
  token: string | null,
  expiresInSeconds?: number,
): void {
  accessToken = token;
  expiresAt =
    token && expiresInSeconds ? Date.now() + expiresInSeconds * 1000 : 0;
}

/**
 * Milliseconds until the access token expires; 0 once it has.
 *
 * Used to renew *before* the token dies. Waiting for a 401 works, but it costs
 * a failed request first and turns every page load after a long idle into a
 * burst of them.
 */
export function millisUntilExpiry(): number {
  if (!accessToken || !expiresAt) return 0;
  return Math.max(0, expiresAt - Date.now());
}

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

/** Notified when a background refresh produces a new session. */
export function setSessionRefreshedHandler(
  handler: ((session: unknown) => void) | null,
): void {
  refreshedHandler = handler;
}

export function onUnauthorized(): void {
  unauthorizedHandler?.();
}

export function onSessionRefreshed(session: unknown): void {
  refreshedHandler?.(session);
}
